// Default timezone for all restaurants
export const DEFAULT_TIMEZONE = 'America/Puerto_Rico';

// Order statuses
export const ORDER_STATUS = {
  SCHEDULED: 'SCHEDULED',
  PENDING: 'PENDING',
  PREPARING: 'PREPARING',
  READY: 'READY',
  SHIPPED: 'SHIPPED',
  DELIVERED: 'DELIVERED',
  CANCELED: 'CANCELED',
} as const;

export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];

// Order types
export const ORDER_TYPE = {
  PICKUP: 'PICKUP',
  DELIVERY: 'DELIVERY',
} as const;

export type OrderType = (typeof ORDER_TYPE)[keyof typeof ORDER_TYPE];

// Who canceled the order
export const CANCELED_BY = {
  CUSTOMER: 'CUSTOMER',
  RESTAURANT: 'RESTAURANT',
} as const;

export type CanceledBy = (typeof CANCELED_BY)[keyof typeof CANCELED_BY];

// Restaurant plans
export const PLAN = {
  FREE: 'FREE',
  BASIC: 'BASIC',
  PRO: 'PRO',
} as const;

export type Plan = (typeof PLAN)[keyof typeof PLAN];

// Status colors for Telegram messages
export const STATUS_EMOJI = {
  SCHEDULED: '📅',
  PENDING: '🟥',
  PREPARING: '🟨',
  READY: '🟩',
  SHIPPED: '🚚',
  DELIVERED: '✅',
  CANCELED: '⬛',
} as const;

// State labels in Spanish
export const STATUS_LABEL = {
  SCHEDULED: 'PROGRAMADA',
  PENDING: 'PENDIENTE',
  PREPARING: 'PREPARANDO',
  READY: 'LISTA',
  SHIPPED: 'EN CAMINO',
  DELIVERED: 'ENTREGADA',
  CANCELED: 'CANCELADA',
} as const;

// Valid state transitions
export const VALID_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  SCHEDULED: ['PREPARING', 'CANCELED'],
  PENDING: ['PREPARING', 'CANCELED'],
  PREPARING: ['READY', 'CANCELED'],
  READY: ['SHIPPED'],
  SHIPPED: ['DELIVERED'],
  DELIVERED: [],
  CANCELED: [],
};

// ============================================
// Price Conversion
// ============================================

/**
 * Conversion factor for cents to dollars.
 * Prices are stored in cents in the database and converted to dollars for display.
 */
export const CENTS_PER_DOLLAR = 100;

// ============================================
// Image Limits Configuration
// ============================================

export const IMAGE_LIMITS = {
  MAX_IMAGES_PER_RESTAURANT: 250,
  MAX_FILE_SIZE_MB: 5,
  MAX_FILE_SIZE_BYTES: 5 * 1024 * 1024, // 5MB in bytes
  COMPRESSION_TARGET_MB: 0.5, // 500KB target after compression
  MAX_WIDTH_OR_HEIGHT: 800, // Max dimension in pixels
} as const;

// ============================================
// Anti-Fraud / Order Limits Configuration
// ============================================

// Default order limits (can be overridden per restaurant)
export const ORDER_LIMITS = {
  // Rate limiting
  MAX_ORDERS_PER_HOUR: 3,
  MAX_ORDERS_PER_DAY: 10,
  MIN_SECONDS_BETWEEN_ORDERS: 300, // 5 minutes

  // Order size limits (in cents, e.g., 500 = $5.00)
  MIN_ORDER_AMOUNT: 500, // Minimum order amount: $5.00
  MAX_ORDER_AMOUNT: 50000, // Maximum order amount: $500.00
  MAX_ITEMS_PER_ORDER: 20,
  MAX_QUANTITY_PER_ITEM: 10,

  // Cancellation grace period (seconds)
  CANCELLATION_GRACE_PERIOD: 180, // 3 minutes
} as const;

// Customer trust score thresholds
export const TRUST_SCORE = {
  MIN: 0,
  MAX: 100,
  DEFAULT: 50,
  BLOCKED_THRESHOLD: 5, // Below this, customer is effectively blocked (lowered for MVP)
  TRUSTED_THRESHOLD: 80, // Above this, skip some validations
} as const;

// Trust score adjustments (relaxed for MVP - can be tightened later)
export const TRUST_ADJUSTMENTS = {
  ORDER_COMPLETED: 5, // +5 for completed order
  ORDER_CANCELED_BY_CUSTOMER: -5, // -5 for customer cancellation (was -10)
  ORDER_CANCELED_AFTER_GRACE: -10, // -10 for cancellation after grace period (was -20)
  ORDER_CANCELED_BY_RESTAURANT: 0, // No penalty when restaurant cancels (not customer's fault)
  FIRST_ORDER_BONUS: 10, // +10 for first completed order
} as const;

// Days of week for opening hours
export const DAYS_OF_WEEK = {
  MONDAY: 'MONDAY',
  TUESDAY: 'TUESDAY',
  WEDNESDAY: 'WEDNESDAY',
  THURSDAY: 'THURSDAY',
  FRIDAY: 'FRIDAY',
  SATURDAY: 'SATURDAY',
  SUNDAY: 'SUNDAY',
} as const;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[keyof typeof DAYS_OF_WEEK];

// Opening hours time slot interface
export interface TimeSlot {
  open: string; // "HH:MM" format (24h)
  close: string; // "HH:MM" format (24h)
}

export interface OpeningHours {
  [key: string]: TimeSlot[] | null; // null means closed that day
}

// Time constants
export * from './time.ts';

// ============================================
// Payment Constants
// ============================================

// Payment methods available at checkout
export const PAYMENT_METHOD = {
  CASH: 'CASH',
  CARD: 'CARD',
} as const;

export type PaymentMethod = (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD];

// Payment status for orders with card payment
export const PAYMENT_STATUS = {
  PENDING: 'PENDING',     // PaymentIntent created, waiting for payment
  PAID: 'PAID',           // Payment succeeded
  FAILED: 'FAILED',       // Payment failed
  REFUNDED: 'REFUNDED',   // Payment was refunded via Stripe
  DISPUTED: 'DISPUTED',   // Customer opened a dispute/chargeback
} as const;

export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

// Stripe Connect account status
export const STRIPE_CONNECT_STATUS = {
  ONBOARDING: 'ONBOARDING',       // Account created, onboarding not complete
  ACTIVE: 'ACTIVE',               // Onboarding complete, charges enabled
  RESTRICTED: 'RESTRICTED',       // Account restricted, needs attention
} as const;

export type StripeConnectStatus = (typeof STRIPE_CONNECT_STATUS)[keyof typeof STRIPE_CONNECT_STATUS];

// Platform fee configuration
export const PLATFORM_FEE = {
  PERCENTAGE: 5, // 5% platform fee on each transaction
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// External API URLs
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Stripe API base URL for all API requests.
 * @see https://stripe.com/docs/api
 */
export const STRIPE_API_BASE = 'https://api.stripe.com/v1';

/**
 * Telegram Bot API base URL (append bot token after).
 * Usage: `${TELEGRAM_BOT_API_BASE}${botToken}/methodName`
 * @see https://core.telegram.org/bots/api
 */
export const TELEGRAM_BOT_API_BASE = 'https://api.telegram.org/bot';


// ─── QR Code Templates ─────────────────────────────────────────────

/** Available QR template identifiers */
export type QRTemplateId = 'basic' | 'order-here' | 'table' | 'sticker';

/** Metadata about each template shown in the UI */
export interface QRTemplateInfo {
  id: QRTemplateId;
  name: string;
  description: string;
  /** Whether this template requires a table number */
  requiresTableNumber: boolean;
}

/** Registry of available QR templates */
export const QR_TEMPLATES: QRTemplateInfo[] = [
  { id: 'basic', name: 'Básico', description: 'Solo el código QR', requiresTableNumber: false },
  { id: 'order-here', name: 'Ordena Aquí', description: 'QR con texto "¡Ordena Aquí!"', requiresTableNumber: false },
  { id: 'table', name: 'Mesa', description: 'QR con número de mesa', requiresTableNumber: true },
  { id: 'sticker', name: 'Sticker', description: 'Diseño circular para pegar', requiresTableNumber: false },
];